import { type Component } from 'vue';

// Currency options
export const DEFAULT_CURRENCY = 'USD';
export const DEFAULT_CURRENCY_DISPLAY = 'code';
export const CURRENCY_DISPLAY_OPTIONS = ['code', 'symbol', 'narrowSymbol', 'name', 'hidden'] as const;

export type CurrencyDisplay = (typeof CURRENCY_DISPLAY_OPTIONS)[number];

// Backend default fields
export const DEFAULT_FIELDS = [
  'id',
  'first_name',
  'last_name',
  'co_employee_id',
  'start_date',
  'gender',
  'race_ethnicity',
  'job_title',
  'job_family_group',
  'job_family',
  'level',
  'geo',
  'region',
  'location',
  'local_currency',
  'notes',
  'eligible',
  'rating',
  'promotion',
  'new_job_title',
  'new_level',
  'current_salary',
  'salary_increase',
  'salary_increase_recommended',
  'salary_increase_recommended_override',
  'salary_increase_budget',
  'salary_increase_budget_recommended',
  'annual_bonus',
  'annual_bonus_recommended',
  'annual_bonus_recommended_override',
  'promo_equity',
  'promo_equity_recommended',
  'promo_equity_recommended_override',
  'refresh_equity',
  'refresh_equity_recommended',
  'refresh_equity_recommended_override',
  'spot_equity',
  'spot_equity_recommended',
  'spot_equity_recommended_override',
  'spot_bonus',
  'spot_bonus_recommended',
  'spot_bonus_recommended_override',
  'range_salary_low',
  'range_salary_mid',
  'range_salary_high',
  'has_comments',
  // UI Fields below here - These fields have a special handling in the UI
  // The first column in the employees table, it combines `first_name` and `last_name`
  'Employee Name',
  // A calculated column
  'New Salary',
  // A calculated column
  'New Compa Ratio',
] as const;

export type DefaultFieldName = (typeof DEFAULT_FIELDS)[number];

/**
 * @const
 * The comp elements
 */
export const COMP_ELEMENTS = [
  'spot_equity',
  'refresh_equity',
  'promo_equity',
  'spot_bonus',
  'annual_bonus',
  'salary_increase',
] as const;

/**
 * @const
 * The comp elements special fields
 */
export const COMP_ELEMENTS_SPECIAL_FIELDS = [
  'spot_equity_recommended',
  'spot_equity_recommended_override',
  'refresh_equity_recommended',
  'refresh_equity_recommended_override',
  'promo_equity_recommended',
  'promo_equity_recommended_override',
  'spot_bonus_recommended',
  'spot_bonus_recommended_override',
  'annual_bonus_recommended',
  'annual_bonus_recommended_override',
  'salary_increase_recommended',
  'salary_increase_recommended_override',
] as const;

/**
 * @const
 * Default page size for the pagination in tables
 */
export const DEFAULT_PAGE_SIZE = 100;

/**
 * Default truncate options
 *
 * @type {{length: number, omission: string, lines: number}}
 */
export const TRUNCATE_OPTIONS = { length: 250, omission: '...', lines: 6 } as const;

// Frontend supported field types
export const FIELD_TYPES = [
  'number',
  'inputNumber',
  'currency',
  'inputCurrency',
  'inputCurrencyInfo',
  'date',
  'inputDate',
  'boolean',
  'inputBoolean',
  'textLong',
  'inputTextLong',
  'text',
  'inputText',
  'selectText',
  'inputSelectText',
  'selectTextMultiple',
  'inputSelectTextMultiple',
  'percent',
  'inputPercent',
  'inputImage',
] as const;

export type FieldType = (typeof FIELD_TYPES)[number];

// Generic used for field to Vue component mapping
export type ComponentFieldMap = {
  readonly [key in FieldType]: Component;
};

// The backend supported types
export type BackendDataDisplayTypeKey =
  | 'string_value'
  | 'boolean_value'
  | 'text_value'
  | 'date_value'
  | 'integer_value'
  | 'decimal_value'
  | 'currency_value'
  | 'percent_value'
  | 'wave';

// This map defines how fields are displayed inside the edit dropdown
export const BACKEND_DATA_INPUT_TYPE_MAP = {
  string_value: 'inputText',
  boolean_value: 'inputBoolean',
  text_value: 'inputTextLong',
  date_value: 'inputDate',
  integer_value: 'inputNumber',
  decimal_value: 'inputNumber',
  currency_value: 'inputCurrency',
  percent_value: 'inputPercent',
  wave: 'inputSelectText',
} as const satisfies { [key in BackendDataDisplayTypeKey]: FieldType };

// This map defines how fields are displayed inside the table cells
export const BACKEND_DATA_DISPLAY_TYPE_MAP = {
  string_value: 'text',
  boolean_value: 'boolean',
  text_value: 'textLong',
  date_value: 'date',
  integer_value: 'number',
  decimal_value: 'number',
  currency_value: 'currency',
  percent_value: 'percent',
  wave: 'selectText',
} as const satisfies { [key in BackendDataDisplayTypeKey]: FieldType };

// Fields that need to be excluded for group view
export const GROUP_VIEW_EXCLUDED_FIELDS = [
  'salary_increase_recommended_override',
  'spot_equity_recommended_override',
  'annual_bonus_recommended_override',
  'spot_bonus_recommended_override',
  'promo_equity_recommended_override',
  'refresh_equity_recommended_override',
] as const satisfies readonly DefaultFieldName[];

export type GroupViewExcludedField = (typeof GROUP_VIEW_EXCLUDED_FIELDS)[number];

// Fields that have a "parent" field and they get displayed as groups in the frontend
export const LINKED_FIELDS = {
  salary_increase_recommended_override: 'salary_increase',
  annual_bonus_recommended_override: 'annual_bonus',
  spot_equity_recommended_override: 'spot_equity',
  refresh_equity_recommended_override: 'refresh_equity',
  spot_bonus_recommended_override: 'spot_bonus',
  promo_equity_recommended_override: 'promo_equity',
} as const satisfies Partial<{ [K in DefaultFieldName]: DefaultFieldName }>;

export type LinkedFieldKey = keyof typeof LINKED_FIELDS;

// The API user types
export type ConfigApiUserRole = 'admin' | 'planner';

// The API layout names
export const CONFIG_API_LAYOUT_NAME = ['employees'] as const;

export type ConfigApiLayoutName = (typeof CONFIG_API_LAYOUT_NAME)[number];

/**
 * A map for default widths per column type
 */
export const FIELD_TYPE_WIDTH_MAP: { [key in BackendDataDisplayTypeKey]: string } = {
  string_value: '200px',
  boolean_value: '120px',
  text_value: '300px',
  date_value: '150px',
  integer_value: '150px',
  decimal_value: '150px',
  currency_value: '150px',
  percent_value: '120px',
  wave: '200px',
};

/*
The list of permissions that can be assigned to a role
*/
export const PERMISSIONS = [
  'can_view_comments',
  'can_create_comments',
  'can_view_change_history',
  'can_download_reward_letters',
  'can_send_reward_letters',
  'can_approve_group',
  'can_view_budgets',
  'can_export_data',
  'can_copy_or_sync_employees',
] as const;

export const SAVED_EXPORT_TEMPLATE_TYPES = [
  'admin_layout',
  'admin_layout_recommended',
  'admin_layout_recommended_created_at_order',
] as const;
